import React from 'react';
import { Col, Row } from 'antd';
import { useQuery } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  Currency,
  ErrorHandler,
  Gutter,
  Title,
  Text,
  Divider,
} from '@common/components';
import { ENUM_QUERY } from '@common/graphql';
import { StaffingRequestTemplateType } from '@jobs/graphql';
import { BillingCardType } from '@institutions/graphql';

type BillingCardRateByShiftType = {
  travelerRate?: number;
  localRate?: number;
  directSourcingRate?: number;
};

type EstimatedCost = {
  lowest: number;
  highest: number;
};

type StaffingRequestTemplateDetailsProps = {
  staffingRequestTemplate: StaffingRequestTemplateType;
  billingCard?: BillingCardType;
  billingCardRateByShiftType?: BillingCardRateByShiftType | null;
  estimatedCost: EstimatedCost;
};

export function StaffingRequestTemplateDetails({
  staffingRequestTemplate,
  billingCard,
  billingCardRateByShiftType,
  estimatedCost,
}: StaffingRequestTemplateDetailsProps) {
  const currentMembership = useCurrentMembership();
  const canSeeRates = currentMembership.canSeeStaffingRequestRates;

  const { data: staffingRequestTypeData, error: staffingRequestTypeError } =
    useQuery(ENUM_QUERY, {
      variables: { key: 'StaffingRequestTypes' },
    });

  if (staffingRequestTypeError) {
    return (
      <ErrorHandler
        error={staffingRequestTypeError}
        log="StaffingRequestTemplateDetails"
        messageType="contextual"
      />
    );
  }

  const specialties =
    staffingRequestTemplate?.staffingRequestTemplateSpecialties?.map(
      (specialtyAssoc) => ({
        id: specialtyAssoc.clinicalSpecialty.id,
        name: specialtyAssoc.clinicalSpecialty.displayName,
        necessityLevel: specialtyAssoc.necessityLevel,
      })
    );

  const technologies =
    staffingRequestTemplate?.staffingRequestTemplateTechnologies?.map(
      (technologyAssoc) => ({ name: technologyAssoc.technology.name })
    );

  const certifications =
    staffingRequestTemplate?.staffingRequestTemplateCertifications?.map(
      (certificationAssoc) => ({
        credential: certificationAssoc.certification.credential,
        necessityLevel: certificationAssoc.necessityLevel,
      })
    );

  return (
    <>
      <Gutter direction="bottom">
        <Title size="h4">Position Details</Title>
      </Gutter>
      <Row align="middle" gutter={[4, 4]}>
        <Col span={8}>
          <Text secondary>Name</Text>
        </Col>
        <Col span={16}>
          <Text>{staffingRequestTemplate.name}</Text>
        </Col>

        <Col span={8}>
          <Text secondary>Facility</Text>
        </Col>
        <Col span={16}>
          <Text>
            {
              staffingRequestTemplate?.organizationUnit?.department
                ?.organizationBusiness.name
            }
          </Text>
        </Col>

        <Col span={8}>
          <Text secondary>Unit</Text>
        </Col>
        <Col span={16}>
          <Text>{staffingRequestTemplate?.organizationUnit?.name}</Text>
        </Col>

        <Col span={8}>
          <Text secondary>Role</Text>
        </Col>
        <Col span={16}>
          <Text>{staffingRequestTemplate?.role?.abbreviation}</Text>
        </Col>

        <Col span={8}>
          <Text secondary>Cost Center</Text>
        </Col>
        <Col span={16}>
          <Text>{staffingRequestTemplate?.organizationUnit?.costCenter}</Text>
        </Col>

        <Col span={8}>
          <Text secondary>Contract Type</Text>
        </Col>
        <Col span={16}>
          <Text>
            {
              staffingRequestTypeData?.enum?.find(
                ({ name }) =>
                  name === staffingRequestTemplate.staffingRequestType
              )?.description
            }
          </Text>
        </Col>
      </Row>

      <Divider gutter={12} />

      {canSeeRates && (
        <>
          <Gutter direction="bottom">
            <Title size="h4">Rate Info</Title>
          </Gutter>
          <Row gutter={[4, 4]}>
            <Col span={8}>
              <Text secondary>Base Template</Text>
            </Col>
            <Col span={16}>
              <Text deleted={!!billingCard} secondary={!!billingCard}>
                <Currency
                  value={staffingRequestTemplate?.billingRegularRate || 0}
                />
              </Text>
            </Col>

            {billingCard && (
              <>
                <Col span={8}>
                  <Text secondary>Traveler</Text>
                </Col>
                <Col span={16}>
                  <Currency
                    value={billingCardRateByShiftType?.travelerRate || 0}
                  />
                </Col>

                <Col span={8}>
                  <Text secondary>Local</Text>
                </Col>
                <Col span={16}>
                  <Currency
                    value={billingCardRateByShiftType?.localRate || 0}
                  />
                </Col>

                <Col span={8}>
                  <Text secondary>Direct Sourcing</Text>
                </Col>
                <Col span={16}>
                  <Currency
                    value={billingCardRateByShiftType?.directSourcingRate || 0}
                  />
                </Col>
              </>
            )}

            <Col span={8}>
              <Text secondary>Estimated Cost</Text>
            </Col>
            <Col span={16} className="estimated-cost">
              {estimatedCost.lowest === estimatedCost.highest ? (
                <Currency value={estimatedCost.lowest} />
              ) : (
                <>
                  <Currency value={estimatedCost.lowest} /> -{' '}
                  <Currency value={estimatedCost.highest} />
                </>
              )}
            </Col>
          </Row>
        </>
      )}

      <Divider gutter={12} />

      <Gutter direction="bottom">
        <Title size="h4">Experience</Title>
      </Gutter>
      <Row gutter={[4, 4]}>
        <Col span={8}>
          <Text secondary>Specialties</Text>
        </Col>
        <Col span={16} className="multi-line-description-list">
          {specialties?.map(({ id, name, necessityLevel }) => (
            <React.Fragment key={id}>
              <Text>{name} </Text>
              <Text secondary>({necessityLevel})</Text>
              <br />{' '}
            </React.Fragment>
          ))}
          {!specialties?.length && <>N/A</>}
        </Col>

        <Col span={8}>
          <Text secondary>Technologies</Text>
        </Col>
        <Col span={16}>
          {technologies?.map(({ name }) => (
            <React.Fragment key={name}>
              {name} <br />{' '}
            </React.Fragment>
          ))}
        </Col>
      </Row>

      <Divider gutter={12} />

      <Gutter direction="bottom">
        <Title size="h4">Certifications</Title>
      </Gutter>
      <Row gutter={[4, 4]}>
        <Col span={8}>
          <Text secondary>Certifications</Text>
        </Col>
        <Col span={16}>
          {certifications?.map(({ credential, necessityLevel }) => (
            <React.Fragment key={credential}>
              <Text>{credential} </Text>
              <Text secondary>({necessityLevel})</Text>
              <br />{' '}
            </React.Fragment>
          ))}
          {!certifications?.length && <>N/A</>}
        </Col>
      </Row>
    </>
  );
}
